// This file is auto-generated by @hey-api/openapi-ts

export type ClientOptions = {
    baseUrl: `${string}://${string}` | (string & {});
};

/**
 * Individual component difference in a composition
 */
export type ComponentDiff = {
    /**
     * Name of the organization
     */
    organization_name: string;
    /**
     * ISIN identifier for the holding
     */
    isin: string;
    /**
     * Status of the component (UNCHANGED, ADDED, or REMOVED)
     *
     * * `UNCHANGED` - UNCHANGED
     * * `ADDED` - ADDED
     * * `REMOVED` - REMOVED
     */
    status: StatusEnum;
    /**
     * Reasons why the component was filtered
     */
    filter_reasons: Array<FilterReasonsEnum>;
};

/**
 * Individual component difference in a composition
 */
export type ComponentDiffRequest = {
    /**
     * Name of the organization
     */
    organization_name: string;
    /**
     * ISIN identifier for the holding
     */
    isin: string;
    /**
     * Status of the component (UNCHANGED, ADDED, or REMOVED)
     *
     * * `UNCHANGED` - UNCHANGED
     * * `ADDED` - ADDED
     * * `REMOVED` - REMOVED
     */
    status: StatusEnum;
    /**
     * Reasons why the component was filtered
     */
    filter_reasons: Array<FilterReasonsEnum>;
};

/**
 * Response serializer for composition differences
 */
export type CompositionDifferenceResponse = {
    /**
     * List of component differences
     */
    components: Array<ComponentDiff>;
};

/**
 * Response serializer for composition differences
 */
export type CompositionDifferenceResponseRequest = {
    /**
     * List of component differences
     */
    components: Array<ComponentDiffRequest>;
};

/**
 * Serializer for export result response.
 */
export type ExportResult = {
    /**
     * S3 bucket name where the exported CSV file is stored
     */
    s3_bucket: string;
    /**
     * S3 key (path) of the exported CSV file
     */
    s3_key: string;
    /**
     * Number of rows exported (0 if unavailable)
     */
    row_count: number;
    /**
     * Timestamp when export was executed
     */
    exported_at: Date;
};

/**
 * * `NEW_PORTFOLIO_INVESTMENT` - NEW_PORTFOLIO_INVESTMENT
 * * `NOT_HELD_IN_PORTFOLIO` - NOT_HELD_IN_PORTFOLIO
 * * `POA_MARKET` - POA_MARKET
 * * `NON_SPLIT_VOTING_COUNTRY` - NON_SPLIT_VOTING_COUNTRY
 */
export const FilterReasonsEnum = {
    NEW_PORTFOLIO_INVESTMENT: 'NEW_PORTFOLIO_INVESTMENT',
    NOT_HELD_IN_PORTFOLIO: 'NOT_HELD_IN_PORTFOLIO',
    POA_MARKET: 'POA_MARKET',
    NON_SPLIT_VOTING_COUNTRY: 'NON_SPLIT_VOTING_COUNTRY'
} as const;

/**
 * * `NEW_PORTFOLIO_INVESTMENT` - NEW_PORTFOLIO_INVESTMENT
 * * `NOT_HELD_IN_PORTFOLIO` - NOT_HELD_IN_PORTFOLIO
 * * `POA_MARKET` - POA_MARKET
 * * `NON_SPLIT_VOTING_COUNTRY` - NON_SPLIT_VOTING_COUNTRY
 */
export type FilterReasonsEnum = typeof FilterReasonsEnum[keyof typeof FilterReasonsEnum];

/**
 * Serializer for Holding.
 */
export type Holding = {
    /**
     * Unique identifier for the holdings record
     */
    readonly id: string;
    /**
     * ISIN (International Securities Identification Number). Must be 12-character alphanumeric code.
     */
    isin: string;
    /**
     * Number of shares held
     */
    settled_quantity: number;
    /**
     * CUSIP (Committee on Uniform Securities Identification Procedures) number. A 9-character alphanumeric code that identifies North American financial securities.
     */
    cusip?: string | null;
    /**
     * When the holdings record is valid
     */
    valid_time: Date;
    /**
     * When the holdings record was created
     */
    readonly create_time: Date;
    /**
     * When the holdings record was last updated
     */
    readonly update_time: Date;
    /**
     * Reference to the voter owning the holdings
     */
    voter_id: string;
};

/**
 * Serializer for creating holdings (without voter_id).
 */
export type HoldingInputRequest = {
    /**
     * ISIN (International Securities Identification Number). Must be 12-character alphanumeric code.
     */
    isin: string;
    /**
     * Number of shares held
     */
    settled_quantity: number;
    /**
     * CUSIP (Committee on Uniform Securities Identification Procedures) number. A 9-character alphanumeric code that identifies North American financial securities.
     */
    cusip?: string | null;
    /**
     * When the holdings record is valid
     */
    valid_time: Date;
};

/**
 * Serializer for the holdings response wrapper.
 */
export type HoldingResponse = {
    /**
     * List of holdings for the voter
     */
    holdings: Array<Holding>;
};

export type Meeting = {
    /**
     * Unique identifier for the general meeting
     */
    general_meeting_id: string;
    /**
     * Resource name for the general meeting in the format
     * 'organizations/{organization_id}/generalMeetings/{general_meeting_id}'
     */
    readonly general_meeting_name: string;
    /**
     * Resource name for the organization in the format 'organizations/{organization_id}'
     */
    readonly organization_name: string;
    /**
     * Resource name for the meeting ballot in the format
     * 'habitats/{habitat_id}/meetingBallots/{meeting_ballot_id}'
     */
    readonly meeting_ballot_name: string;
    /**
     * Resource name for the scheme in the format 'habitats/{habitat_id}/schemes/{scheme_id}'
     */
    readonly scheme_name: string;
    /**
     * Date and time when the meeting is scheduled
     */
    meeting_date: Date;
    /**
     * Identifier for the habitat this meeting belongs to
     */
    habitat_id: string;
    /**
     * Unique identifier for the organization holding the meeting
     */
    organization_id: string;
    /**
     * Display name of the organization
     */
    organization_display_title: string;
    /**
     * URL to the organization's logo image
     */
    organization_logo_url?: string | null;
    /**
     * NAICS industry classification title for the organization
     */
    organization_naics_title: string;
    /**
     * Identifier for the meeting ballot
     */
    meeting_ballot_id: string;
    /**
     * Deadline for voting/taking action on meeting proposals
     */
    action_deadline: Date;
    /**
     * Identifier for the investment scheme
     */
    scheme_id: string;
    /**
     * List of theme/classification codes for proposals in this meeting (e.g., CC, OE, PR)
     */
    proposal_classification_codes?: Array<string>;
};

export type PaginatedCompositionDifferenceResponseList = {
    next?: string | null;
    previous?: string | null;
    results: Array<CompositionDifferenceResponse>;
};

export type PaginatedMeetingList = {
    next?: string | null;
    previous?: string | null;
    results: Array<Meeting>;
};

export type PaginatedPiBallotSearchList = {
    next?: string | null;
    previous?: string | null;
    results: Array<PiBallotSearch>;
};

export type PaginatedPiMeetingSearchList = {
    next?: string | null;
    previous?: string | null;
    results: Array<PiMeetingSearch>;
};

export type PaginatedSplitVoteInstructionOverwriteReasonList = {
    next?: string | null;
    previous?: string | null;
    results: Array<SplitVoteInstructionOverwriteReason>;
};

/**
 * Serializer for PI ballot search results.
 */
export type PiBallotSearch = {
    /**
     * Unique identifier for this specific version of the ballot
     */
    ballot_version_id: string;
    /**
     * Resource name for the proxy intermediary ballot in the format used by the workflow system.
     */
    readonly proxy_intermediary_ballot_name: string;
    /**
     * Resource name for the proxy intermediary meeting in the format used by the workflow system.
     */
    readonly proxy_intermediary_meeting_name: string;
    /**
     * Total number of shares available for voting on this ballot
     */
    available_shares: number;
    /**
     * Unique identity identifier for the ballot across all versions
     */
    ballot_identity_id: string;
    /**
     * External system identifier for the ballot (e.g., from proxy intermediary platform)
     */
    ballot_external_id: string;
    /**
     * Indicates if this is the most recent active version of the ballot
     */
    ballot_version_is_current: boolean;
    /**
     * Total number of ballots associated with the same proxy intermediary meeting
     */
    ballots_per_meeting: number;
    /**
     * Internal identifier for the custodian account holding the shares
     */
    custodian_account_id: string;
    /**
     * Account number assigned by the custodian institution
     */
    custodian_account_number: string;
    /**
     * Descriptive name or title of the custodian account
     */
    custodian_account_title: string;
    /**
     * Indicates if the ballot aligns with the fund manager's voting policy
     */
    fund_manager_policy_supported: boolean;
    /**
     * Name of the fund manager organization that manages the fund
     */
    fund_manager_title?: string | null;
    /**
     * UUID of the fund manager record
     */
    fund_manager_id?: string | null;
    /**
     * Name of the investment fund that owns the shares being voted
     */
    fund_title: string;
    /**
     * Official name of the shareholder meeting (e.g., Annual General Meeting 2024)
     */
    general_meeting_name: string;
    /**
     * Indicates if the ballot aligns with investor voting guidelines
     */
    investor_supported: boolean;
    /**
     * Indicates if the ballot is supported by formal investor policy recommendations
     */
    investor_policy_supported: boolean;
    /**
     * Name of the company or organization holding the shareholder meeting
     */
    organization_title: string;
    /**
     * CUSIP, ISIN, or other public identifier for the security being voted on
     */
    owned_public_security_id: string;
    /**
     * Display name of the proxy intermediary service provider
     */
    proxy_intermediary_title: string;
    /**
     * Meeting identifier where this ballot is being voted on
     */
    proxy_intermediary_meeting_id: string;
    /**
     * Identifier for the proxy intermediary managing this ballot
     */
    proxy_intermediary_id: string;
    /**
     * Indicates if additional validation is needed for split voting scenarios
     */
    split_vote_check_required: boolean;
    /**
     * Cutoff date and time for submitting votes on this ballot
     */
    vote_deadline: Date;
    /**
     * Current state in the voting workflow (e.g. pending, submitted, checked)
     */
    workflow_status: string;
    /**
     * Username of the person who submitted the vote for processing
     */
    workflow_submitter: string;
    /**
     * Username of the person who reviewed and approved the vote submission
     */
    workflow_checker: string;
    /**
     * UUID of the fund record, used to construct the fund resource name
     */
    fund_id?: string | null;
};

/**
 * Serializer for PI meeting search results.
 */
export type PiMeetingSearch = {
    /**
     * Unique identifier for this specific version of the meeting
     */
    meeting_version_id: string;
    /**
     * Resource name for the proxy intermediary meeting in the format used by the workflow system.
     */
    readonly proxy_intermediary_meeting_name: string;
    /**
     * Unique identity identifier for the meeting across all versions
     */
    meeting_identity_id: string;
    /**
     * Indicates if this is the most recent active version of the meeting
     */
    meeting_version_is_current: boolean;
    /**
     * Meeting identifier within the proxy intermediary system
     */
    proxy_intermediary_meeting_id: string;
    /**
     * Identifier for the proxy intermediary managing this meeting
     */
    proxy_intermediary_id: string;
    /**
     * External system identifier for the meeting (e.g., from proxy intermediary platform)
     */
    meeting_external_id: string;
    /**
     * Name of the company or organization holding the shareholder meeting
     */
    organization_title: string;
    /**
     * Official name of the shareholder meeting (e.g., Annual General Meeting 2024)
     */
    transparency_general_meeting_name: string;
    /**
     * Type of meeting (e.g., Annual General Meeting, Extraordinary General Meeting)
     */
    meeting_type: string;
    /**
     * Date and time when the meeting is scheduled to occur
     */
    meeting_time: Date;
    /**
     * Cutoff date and time for submitting votes for this meeting
     */
    submission_deadline: Date;
    /**
     * Record date for determining shareholder eligibility
     */
    record_date?: Date | null;
    /**
     * Indicates if the meeting contains contested proposals
     */
    is_contested: boolean;
    /**
     * Indicates if split voting is supported for this meeting
     */
    is_split_voting_supported?: boolean | null;
    /**
     * Additional comments or notes about the meeting
     */
    meeting_comment?: string | null;
    /**
     * Reason why the meeting might not be supported
     */
    unsupported_reason?: string | null;
    /**
     * Date and time when the meeting was cancelled, if applicable
     */
    cancel_time?: Date | null;
    /**
     * Array of security identifiers associated with this meeting
     */
    security_ids?: Array<string> | null;
    /**
     * Identifier for the voting operations workflow
     */
    workflow_id?: string | null;
    /**
     * Current state in the voting workflow (e.g. new, ready_for_refinement, successful)
     */
    workflow_status?: string | null;
    /**
     * Username of the person assigned to refine the meeting data
     */
    workflow_refiner?: string | null;
    /**
     * Username of the person assigned to check the meeting data
     */
    workflow_checker?: string | null;
    /**
     * Display name of the proxy intermediary service provider
     */
    proxy_intermediary_title?: string | null;
    /**
     * Indicates if any of the meeting's securities are supported by investor policies
     */
    investor_supported?: boolean;
    /**
     * Indicates if any of the meeting's securities are supported by investor policy guidelines
     */
    investor_policy_supported?: boolean;
    /**
     * Indicates if any of the meeting's securities are supported by fund manager policies
     */
    fund_manager_policy_supported?: boolean;
    /**
     * Number of ballots associated with this meeting
     */
    ballot_count: number;
    /**
     * Indicates if the meeting has associated ballots available for voting
     */
    has_ballots: boolean;
    /**
     * Date and time when the meeting was first created in the proxy intermediary system
     */
    meeting_create_time: Date;
    /**
     * Latest valid_to date across all SIIs for this meeting's securities, or NULL if any SII has no expiry
     */
    latest_valid_to_sii?: Date | null;
};

/**
 * Serializer for a policy.
 */
export type Policy = {
    /**
     * Unique identifier for the policy
     */
    readonly id: string;
    /**
     * Identifier to be shared externally with integrated parties
     */
    readonly external_id: string;
    /**
     * Title of the voting policy
     */
    title: string;
    /**
     * Title of the voting policy provider
     */
    provider_title: string;
    /**
     * Description of the voting policy in formatted text
     */
    formatted_description?: string | null;
    /**
     * Key referencing the document associated with the voting policy
     */
    document_key?: string | null;
    /**
     * Indicates if the selected policy is currently available on direct outreach
     */
    is_available?: boolean | null;
    /**
     * When the voting policy was created
     */
    readonly create_time: Date;
    /**
     * When the voting policy was last updated
     */
    readonly update_time: Date;
};

/**
 * Serializer for a policy.
 */
export type PolicyRequest = {
    /**
     * Title of the voting policy
     */
    title: string;
    /**
     * Title of the voting policy provider
     */
    provider_title: string;
    /**
     * Description of the voting policy in formatted text
     */
    formatted_description?: string | null;
    /**
     * Key referencing the document associated with the voting policy
     */
    document_key?: string | null;
    /**
     * Indicates if the selected policy is currently available on direct outreach
     */
    is_available?: boolean | null;
};

/**
 * Serializer for policy response.
 */
export type PolicyResponse = {
    /**
     * Unique identifier for the policy
     */
    readonly id: string;
    /**
     * Identifier to be shared externally with integrated parties
     */
    external_id: string;
    /**
     * Title of the voting policy
     */
    title: string;
    /**
     * Title of the voting policy provider
     */
    provider_title: string;
    /**
     * Description of the voting policy in formatted text
     */
    formatted_description?: string | null;
    /**
     * Key referencing the document associated with the voting policy
     */
    document_key?: string | null;
    /**
     * Indicates if the selected policy is currently available on direct outreach
     */
    is_available?: boolean | null;
    /**
     * When the voting policy was created
     */
    readonly create_time: Date;
    /**
     * When the voting policy was last updated
     */
    readonly update_time: Date;
};

/**
 * * `vote_adjusted_for_voting_restriction` - Vote Adjusted for Voting Restriction
 * * `vote_adjusted_for_contested_meeting` - Vote Adjusted for Contested Meeting
 * * `vote_aligned_with_fm_due_to_restriction` - Vote Aligned with FM Due to Restriction
 */
export const ReportableReasonEnum = {
    VOTE_ADJUSTED_FOR_VOTING_RESTRICTION: 'vote_adjusted_for_voting_restriction',
    VOTE_ADJUSTED_FOR_CONTESTED_MEETING: 'vote_adjusted_for_contested_meeting',
    VOTE_ALIGNED_WITH_FM_DUE_TO_RESTRICTION: 'vote_aligned_with_fm_due_to_restriction'
} as const;

/**
 * * `vote_adjusted_for_voting_restriction` - Vote Adjusted for Voting Restriction
 * * `vote_adjusted_for_contested_meeting` - Vote Adjusted for Contested Meeting
 * * `vote_aligned_with_fm_due_to_restriction` - Vote Aligned with FM Due to Restriction
 */
export type ReportableReasonEnum = typeof ReportableReasonEnum[keyof typeof ReportableReasonEnum];

export type SelectedPolicy = {
    /**
     * Unique identifier for the selected policy record
     */
    readonly id: string;
    /**
     * When the selected policy record was created
     */
    readonly create_time: Date;
    /**
     * When the selected policy record was last updated
     */
    readonly update_time: Date;
    /**
     * Reference to the voter selecting the policy
     */
    voter_id: string;
    /**
     * Reference to the selected policy
     */
    policy_id?: string | null;
};

/**
 * Serializer for creating/updating a selected policy.
 */
export type SelectedPolicyRequestRequest = {
    /**
     * The ID of the policy to select
     */
    policy_id: string;
};

/**
 * Serializer for the selected policy response wrapper.
 */
export type SelectedPolicyResponse = {
    /**
     * The voter's selected policy, or null if none selected
     */
    selectedPolicy: SelectedPolicy | null;
};

/**
 * Serializer for Split Vote Instruction Overwrite Reason.
 */
export type SplitVoteInstructionOverwriteReason = {
    /**
     * Reportable reason for the overwrite
     *
     * * `vote_adjusted_for_voting_restriction` - Vote Adjusted for Voting Restriction
     * * `vote_adjusted_for_contested_meeting` - Vote Adjusted for Contested Meeting
     * * `vote_aligned_with_fm_due_to_restriction` - Vote Aligned with FM Due to Restriction
     */
    reportable_reason: ReportableReasonEnum;
    /**
     * Description of the overwrite reason for internal use
     */
    description: string;
};

/**
 * Serializer for Split Vote Instruction Proposal Overwrite.
 */
export type SplitVoteInstructionProposalOverwrite = {
    /**
     * Unique identifier for the SVI Proposal Overwrite
     */
    readonly id: string;
    /**
     * Internal reason for the overwrite, not reportable
     */
    internal_reason?: string | null;
    /**
     * Author of the overwrite
     */
    author: string;
    /**
     * Investor votes FOR
     */
    investor_votes_for: number;
    /**
     * Investor votes AGAINST
     */
    investor_votes_against: number;
    /**
     * Investor votes ABSTAIN
     */
    investor_votes_abstain: number;
    /**
     * Investor votes WITHHOLD
     */
    investor_votes_withhold: number;
    /**
     * Investor votes NO VOTE
     */
    investor_votes_no_vote: number;
    /**
     * Investor votes ONE YEAR
     */
    investor_votes_one_year: number;
    /**
     * Investor votes TWO YEARS
     */
    investor_votes_two_years: number;
    /**
     * Investor votes THREE YEARS
     */
    investor_votes_three_years: number;
    /**
     * Split votes FOR
     */
    split_votes_for: number;
    /**
     * Split votes AGAINST
     */
    split_votes_against: number;
    /**
     * Split votes ABSTAIN
     */
    split_votes_abstain: number;
    /**
     * Split votes WITHHOLD
     */
    split_votes_withhold: number;
    /**
     * Split votes NO VOTE
     */
    split_votes_no_vote: number;
    /**
     * Split votes ONE YEAR
     */
    split_votes_one_year: number;
    /**
     * Split votes TWO YEARS
     */
    split_votes_two_years: number;
    /**
     * Split votes THREE YEARS
     */
    split_votes_three_years: number;
    /**
     * Percentage of split votes FOR
     */
    split_votes_percentage_for: number;
    /**
     * Percentage of split votes AGAINST
     */
    split_votes_percentage_against: number;
    /**
     * Percentage of split votes ABSTAIN
     */
    split_votes_percentage_abstain: number;
    /**
     * Percentage of split votes WITHHOLD
     */
    split_votes_percentage_withhold: number;
    /**
     * Percentage of split votes NO VOTE
     */
    split_votes_percentage_no_vote: number;
    /**
     * Percentage of split votes ONE YEAR
     */
    split_votes_percentage_one_year: number;
    /**
     * Percentage of split votes TWO YEARS
     */
    split_votes_percentage_two_years: number;
    /**
     * Percentage of split votes THREE YEARS
     */
    split_votes_percentage_three_years: number;
    /**
     * When the overwrite was created
     */
    readonly create_time: Date;
    /**
     * When the overwrite was deleted
     */
    readonly delete_time: Date | null;
    /**
     * Reference to the SVI Proposal being overwritten
     */
    split_vote_instruction_proposal_id: string;
    /**
     * Reportable reason for overwriting the SVI Proposal
     */
    reportable_reason: ReportableReasonEnum;
};

/**
 * Serializer for Split Vote Instruction Proposal Overwrite.
 */
export type SplitVoteInstructionProposalOverwriteRequest = {
    /**
     * Internal reason for the overwrite, not reportable
     */
    internal_reason?: string | null;
    /**
     * Author of the overwrite
     */
    author: string;
    /**
     * Investor votes FOR
     */
    investor_votes_for: number;
    /**
     * Investor votes AGAINST
     */
    investor_votes_against: number;
    /**
     * Investor votes ABSTAIN
     */
    investor_votes_abstain: number;
    /**
     * Investor votes WITHHOLD
     */
    investor_votes_withhold: number;
    /**
     * Investor votes NO VOTE
     */
    investor_votes_no_vote: number;
    /**
     * Investor votes ONE YEAR
     */
    investor_votes_one_year: number;
    /**
     * Investor votes TWO YEARS
     */
    investor_votes_two_years: number;
    /**
     * Investor votes THREE YEARS
     */
    investor_votes_three_years: number;
    /**
     * Split votes FOR
     */
    split_votes_for: number;
    /**
     * Split votes AGAINST
     */
    split_votes_against: number;
    /**
     * Split votes ABSTAIN
     */
    split_votes_abstain: number;
    /**
     * Split votes WITHHOLD
     */
    split_votes_withhold: number;
    /**
     * Split votes NO VOTE
     */
    split_votes_no_vote: number;
    /**
     * Split votes ONE YEAR
     */
    split_votes_one_year: number;
    /**
     * Split votes TWO YEARS
     */
    split_votes_two_years: number;
    /**
     * Split votes THREE YEARS
     */
    split_votes_three_years: number;
    /**
     * Percentage of split votes FOR
     */
    split_votes_percentage_for: number;
    /**
     * Percentage of split votes AGAINST
     */
    split_votes_percentage_against: number;
    /**
     * Percentage of split votes ABSTAIN
     */
    split_votes_percentage_abstain: number;
    /**
     * Percentage of split votes WITHHOLD
     */
    split_votes_percentage_withhold: number;
    /**
     * Percentage of split votes NO VOTE
     */
    split_votes_percentage_no_vote: number;
    /**
     * Percentage of split votes ONE YEAR
     */
    split_votes_percentage_one_year: number;
    /**
     * Percentage of split votes TWO YEARS
     */
    split_votes_percentage_two_years: number;
    /**
     * Percentage of split votes THREE YEARS
     */
    split_votes_percentage_three_years: number;
    /**
     * Reference to the SVI Proposal being overwritten
     */
    split_vote_instruction_proposal_id: string;
    /**
     * Reportable reason for overwriting the SVI Proposal
     */
    reportable_reason: ReportableReasonEnum;
};

/**
 * * `UNCHANGED` - UNCHANGED
 * * `ADDED` - ADDED
 * * `REMOVED` - REMOVED
 */
export const StatusEnum = {
    UNCHANGED: 'UNCHANGED',
    ADDED: 'ADDED',
    REMOVED: 'REMOVED'
} as const;

/**
 * * `UNCHANGED` - UNCHANGED
 * * `ADDED` - ADDED
 * * `REMOVED` - REMOVED
 */
export type StatusEnum = typeof StatusEnum[keyof typeof StatusEnum];

/**
 * * `retail` - Retail
 * * `institutional` - Institutional
 */
export const TypeEnum = { RETAIL: 'retail', INSTITUTIONAL: 'institutional' } as const;

/**
 * * `retail` - Retail
 * * `institutional` - Institutional
 */
export type TypeEnum = typeof TypeEnum[keyof typeof TypeEnum];

/**
 * Serializer for voter details response.
 */
export type VoterDetailsResponse = {
    /**
     * Unique identifier for the voter
     */
    id: string;
    /**
     * Broker associated with the voter
     */
    broker_id: string;
};

/**
 * Serializer for creating or updating a voter with their holdings.
 */
export type VoterWithHoldingsInputRequest = {
    /**
     * Mediants unique identifier for the voter
     */
    mediant_id: string;
    /**
     * Broker associated with the voter
     */
    broker_id: string;
    /**
     * Type of voter
     *
     * * `retail` - Retail
     * * `institutional` - Institutional
     */
    type: TypeEnum;
    /**
     * List of holdings for the voter
     */
    holdings: Array<HoldingInputRequest>;
};

/**
 * Serializer for voter response with holdings.
 */
export type VoterWithHoldingsResponse = {
    /**
     * Unique identifier for a voter
     */
    readonly id: string;
    /**
     * Mediants unique identifier for the voter
     */
    mediant_id: string;
    /**
     * Broker associated with the voter
     */
    broker_id: string;
    /**
     * Type of voter
     *
     * * `retail` - Retail
     * * `institutional` - Institutional
     */
    type: TypeEnum;
    /**
     * Authorization token for the voter
     */
    token: string;
    /**
     * List of holdings for the voter
     */
    readonly holdings: Array<Holding>;
};

/**
 * Serializer for Holding.
 */
export type HoldingWritable = {
    /**
     * ISIN (International Securities Identification Number). Must be 12-character alphanumeric code.
     */
    isin: string;
    /**
     * Number of shares held
     */
    settled_quantity: number;
    /**
     * CUSIP (Committee on Uniform Securities Identification Procedures) number. A 9-character alphanumeric code that identifies North American financial securities.
     */
    cusip?: string | null;
    /**
     * When the holdings record is valid
     */
    valid_time: Date;
    /**
     * Reference to the voter owning the holdings
     */
    voter_id: string;
};

/**
 * Serializer for the holdings response wrapper.
 */
export type HoldingResponseWritable = {
    /**
     * List of holdings for the voter
     */
    holdings: Array<HoldingWritable>;
};

export type MeetingWritable = {
    /**
     * Unique identifier for the general meeting
     */
    general_meeting_id: string;
    /**
     * Date and time when the meeting is scheduled
     */
    meeting_date: Date;
    /**
     * Identifier for the habitat this meeting belongs to
     */
    habitat_id: string;
    /**
     * Unique identifier for the organization holding the meeting
     */
    organization_id: string;
    /**
     * Display name of the organization
     */
    organization_display_title: string;
    /**
     * URL to the organization's logo image
     */
    organization_logo_url?: string | null;
    /**
     * NAICS industry classification title for the organization
     */
    organization_naics_title: string;
    /**
     * Identifier for the meeting ballot
     */
    meeting_ballot_id: string;
    /**
     * Deadline for voting/taking action on meeting proposals
     */
    action_deadline: Date;
    /**
     * Identifier for the investment scheme
     */
    scheme_id: string;
    /**
     * List of theme/classification codes for proposals in this meeting (e.g., CC, OE, PR)
     */
    proposal_classification_codes?: Array<string>;
};

export type PaginatedMeetingListWritable = {
    next?: string | null;
    previous?: string | null;
    results: Array<MeetingWritable>;
};

export type PaginatedPiBallotSearchListWritable = {
    next?: string | null;
    previous?: string | null;
    results: Array<PiBallotSearchWritable>;
};

export type PaginatedPiMeetingSearchListWritable = {
    next?: string | null;
    previous?: string | null;
    results: Array<PiMeetingSearchWritable>;
};

/**
 * Serializer for PI ballot search results.
 */
export type PiBallotSearchWritable = {
    /**
     * Unique identifier for this specific version of the ballot
     */
    ballot_version_id: string;
    /**
     * Total number of shares available for voting on this ballot
     */
    available_shares: number;
    /**
     * Unique identity identifier for the ballot across all versions
     */
    ballot_identity_id: string;
    /**
     * External system identifier for the ballot (e.g., from proxy intermediary platform)
     */
    ballot_external_id: string;
    /**
     * Indicates if this is the most recent active version of the ballot
     */
    ballot_version_is_current: boolean;
    /**
     * Total number of ballots associated with the same proxy intermediary meeting
     */
    ballots_per_meeting: number;
    /**
     * Internal identifier for the custodian account holding the shares
     */
    custodian_account_id: string;
    /**
     * Account number assigned by the custodian institution
     */
    custodian_account_number: string;
    /**
     * Descriptive name or title of the custodian account
     */
    custodian_account_title: string;
    /**
     * Indicates if the ballot aligns with the fund manager's voting policy
     */
    fund_manager_policy_supported: boolean;
    /**
     * Name of the fund manager organization that manages the fund
     */
    fund_manager_title?: string | null;
    /**
     * UUID of the fund manager record
     */
    fund_manager_id?: string | null;
    /**
     * Name of the investment fund that owns the shares being voted
     */
    fund_title: string;
    /**
     * Official name of the shareholder meeting (e.g., Annual General Meeting 2024)
     */
    general_meeting_name: string;
    /**
     * Indicates if the ballot aligns with investor voting guidelines
     */
    investor_supported: boolean;
    /**
     * Indicates if the ballot is supported by formal investor policy recommendations
     */
    investor_policy_supported: boolean;
    /**
     * Name of the company or organization holding the shareholder meeting
     */
    organization_title: string;
    /**
     * CUSIP, ISIN, or other public identifier for the security being voted on
     */
    owned_public_security_id: string;
    /**
     * Display name of the proxy intermediary service provider
     */
    proxy_intermediary_title: string;
    /**
     * Meeting identifier where this ballot is being voted on
     */
    proxy_intermediary_meeting_id: string;
    /**
     * Identifier for the proxy intermediary managing this ballot
     */
    proxy_intermediary_id: string;
    /**
     * Indicates if additional validation is needed for split voting scenarios
     */
    split_vote_check_required: boolean;
    /**
     * Cutoff date and time for submitting votes on this ballot
     */
    vote_deadline: Date;
    /**
     * Current state in the voting workflow (e.g. pending, submitted, checked)
     */
    workflow_status: string;
    /**
     * Username of the person who submitted the vote for processing
     */
    workflow_submitter: string;
    /**
     * Username of the person who reviewed and approved the vote submission
     */
    workflow_checker: string;
    /**
     * UUID of the fund record, used to construct the fund resource name
     */
    fund_id?: string | null;
};

/**
 * Serializer for PI meeting search results.
 */
export type PiMeetingSearchWritable = {
    /**
     * Unique identifier for this specific version of the meeting
     */
    meeting_version_id: string;
    /**
     * Unique identity identifier for the meeting across all versions
     */
    meeting_identity_id: string;
    /**
     * Indicates if this is the most recent active version of the meeting
     */
    meeting_version_is_current: boolean;
    /**
     * Meeting identifier within the proxy intermediary system
     */
    proxy_intermediary_meeting_id: string;
    /**
     * Identifier for the proxy intermediary managing this meeting
     */
    proxy_intermediary_id: string;
    /**
     * External system identifier for the meeting (e.g., from proxy intermediary platform)
     */
    meeting_external_id: string;
    /**
     * Name of the company or organization holding the shareholder meeting
     */
    organization_title: string;
    /**
     * Official name of the shareholder meeting (e.g., Annual General Meeting 2024)
     */
    transparency_general_meeting_name: string;
    /**
     * Type of meeting (e.g., Annual General Meeting, Extraordinary General Meeting)
     */
    meeting_type: string;
    /**
     * Date and time when the meeting is scheduled to occur
     */
    meeting_time: Date;
    /**
     * Cutoff date and time for submitting votes for this meeting
     */
    submission_deadline: Date;
    /**
     * Record date for determining shareholder eligibility
     */
    record_date?: Date | null;
    /**
     * Indicates if the meeting contains contested proposals
     */
    is_contested: boolean;
    /**
     * Indicates if split voting is supported for this meeting
     */
    is_split_voting_supported?: boolean | null;
    /**
     * Additional comments or notes about the meeting
     */
    meeting_comment?: string | null;
    /**
     * Reason why the meeting might not be supported
     */
    unsupported_reason?: string | null;
    /**
     * Date and time when the meeting was cancelled, if applicable
     */
    cancel_time?: Date | null;
    /**
     * Array of security identifiers associated with this meeting
     */
    security_ids?: Array<string> | null;
    /**
     * Identifier for the voting operations workflow
     */
    workflow_id?: string | null;
    /**
     * Current state in the voting workflow (e.g. new, ready_for_refinement, successful)
     */
    workflow_status?: string | null;
    /**
     * Username of the person assigned to refine the meeting data
     */
    workflow_refiner?: string | null;
    /**
     * Username of the person assigned to check the meeting data
     */
    workflow_checker?: string | null;
    /**
     * Display name of the proxy intermediary service provider
     */
    proxy_intermediary_title?: string | null;
    /**
     * Indicates if any of the meeting's securities are supported by investor policies
     */
    investor_supported?: boolean;
    /**
     * Indicates if any of the meeting's securities are supported by investor policy guidelines
     */
    investor_policy_supported?: boolean;
    /**
     * Indicates if any of the meeting's securities are supported by fund manager policies
     */
    fund_manager_policy_supported?: boolean;
    /**
     * Number of ballots associated with this meeting
     */
    ballot_count: number;
    /**
     * Indicates if the meeting has associated ballots available for voting
     */
    has_ballots: boolean;
    /**
     * Date and time when the meeting was first created in the proxy intermediary system
     */
    meeting_create_time: Date;
    /**
     * Latest valid_to date across all SIIs for this meeting's securities, or NULL if any SII has no expiry
     */
    latest_valid_to_sii?: Date | null;
};

/**
 * Serializer for a policy.
 */
export type PolicyWritable = {
    /**
     * Title of the voting policy
     */
    title: string;
    /**
     * Title of the voting policy provider
     */
    provider_title: string;
    /**
     * Description of the voting policy in formatted text
     */
    formatted_description?: string | null;
    /**
     * Key referencing the document associated with the voting policy
     */
    document_key?: string | null;
    /**
     * Indicates if the selected policy is currently available on direct outreach
     */
    is_available?: boolean | null;
};

/**
 * Serializer for policy response.
 */
export type PolicyResponseWritable = {
    /**
     * Identifier to be shared externally with integrated parties
     */
    external_id: string;
    /**
     * Title of the voting policy
     */
    title: string;
    /**
     * Title of the voting policy provider
     */
    provider_title: string;
    /**
     * Description of the voting policy in formatted text
     */
    formatted_description?: string | null;
    /**
     * Key referencing the document associated with the voting policy
     */
    document_key?: string | null;
    /**
     * Indicates if the selected policy is currently available on direct outreach
     */
    is_available?: boolean | null;
};

export type SelectedPolicyWritable = {
    /**
     * Reference to the voter selecting the policy
     */
    voter_id: string;
    /**
     * Reference to the selected policy
     */
    policy_id?: string | null;
};

/**
 * Serializer for the selected policy response wrapper.
 */
export type SelectedPolicyResponseWritable = {
    /**
     * The voter's selected policy, or null if none selected
     */
    selectedPolicy: SelectedPolicyWritable | null;
};

/**
 * Serializer for Split Vote Instruction Proposal Overwrite.
 */
export type SplitVoteInstructionProposalOverwriteWritable = {
    /**
     * Internal reason for the overwrite, not reportable
     */
    internal_reason?: string | null;
    /**
     * Author of the overwrite
     */
    author: string;
    /**
     * Investor votes FOR
     */
    investor_votes_for: number;
    /**
     * Investor votes AGAINST
     */
    investor_votes_against: number;
    /**
     * Investor votes ABSTAIN
     */
    investor_votes_abstain: number;
    /**
     * Investor votes WITHHOLD
     */
    investor_votes_withhold: number;
    /**
     * Investor votes NO VOTE
     */
    investor_votes_no_vote: number;
    /**
     * Investor votes ONE YEAR
     */
    investor_votes_one_year: number;
    /**
     * Investor votes TWO YEARS
     */
    investor_votes_two_years: number;
    /**
     * Investor votes THREE YEARS
     */
    investor_votes_three_years: number;
    /**
     * Split votes FOR
     */
    split_votes_for: number;
    /**
     * Split votes AGAINST
     */
    split_votes_against: number;
    /**
     * Split votes ABSTAIN
     */
    split_votes_abstain: number;
    /**
     * Split votes WITHHOLD
     */
    split_votes_withhold: number;
    /**
     * Split votes NO VOTE
     */
    split_votes_no_vote: number;
    /**
     * Split votes ONE YEAR
     */
    split_votes_one_year: number;
    /**
     * Split votes TWO YEARS
     */
    split_votes_two_years: number;
    /**
     * Split votes THREE YEARS
     */
    split_votes_three_years: number;
    /**
     * Percentage of split votes FOR
     */
    split_votes_percentage_for: number;
    /**
     * Percentage of split votes AGAINST
     */
    split_votes_percentage_against: number;
    /**
     * Percentage of split votes ABSTAIN
     */
    split_votes_percentage_abstain: number;
    /**
     * Percentage of split votes WITHHOLD
     */
    split_votes_percentage_withhold: number;
    /**
     * Percentage of split votes NO VOTE
     */
    split_votes_percentage_no_vote: number;
    /**
     * Percentage of split votes ONE YEAR
     */
    split_votes_percentage_one_year: number;
    /**
     * Percentage of split votes TWO YEARS
     */
    split_votes_percentage_two_years: number;
    /**
     * Percentage of split votes THREE YEARS
     */
    split_votes_percentage_three_years: number;
    /**
     * Reference to the SVI Proposal being overwritten
     */
    split_vote_instruction_proposal_id: string;
    /**
     * Reportable reason for overwriting the SVI Proposal
     */
    reportable_reason: ReportableReasonEnum;
};

/**
 * Serializer for voter response with holdings.
 */
export type VoterWithHoldingsResponseWritable = {
    /**
     * Mediants unique identifier for the voter
     */
    mediant_id: string;
    /**
     * Broker associated with the voter
     */
    broker_id: string;
    /**
     * Type of voter
     *
     * * `retail` - Retail
     * * `institutional` - Institutional
     */
    type: TypeEnum;
    /**
     * Authorization token for the voter
     */
    token: string;
};

export type ApiSchemaRetrieveData = {
    body?: never;
    path?: never;
    query?: {
        format?: 'json' | 'yaml';
        lang?: 'af' | 'ar' | 'ar-dz' | 'ast' | 'az' | 'be' | 'bg' | 'bn' | 'br' | 'bs' | 'ca' | 'ckb' | 'cs' | 'cy' | 'da' | 'de' | 'dsb' | 'el' | 'en' | 'en-au' | 'en-gb' | 'eo' | 'es' | 'es-ar' | 'es-co' | 'es-mx' | 'es-ni' | 'es-ve' | 'et' | 'eu' | 'fa' | 'fi' | 'fr' | 'fy' | 'ga' | 'gd' | 'gl' | 'he' | 'hi' | 'hr' | 'hsb' | 'hu' | 'hy' | 'ia' | 'id' | 'ig' | 'io' | 'is' | 'it' | 'ja' | 'ka' | 'kab' | 'kk' | 'km' | 'kn' | 'ko' | 'ky' | 'lb' | 'lt' | 'lv' | 'mk' | 'ml' | 'mn' | 'mr' | 'ms' | 'my' | 'nb' | 'ne' | 'nl' | 'nn' | 'os' | 'pa' | 'pl' | 'pt' | 'pt-br' | 'ro' | 'ru' | 'sk' | 'sl' | 'sq' | 'sr' | 'sr-latn' | 'sv' | 'sw' | 'ta' | 'te' | 'tg' | 'th' | 'tk' | 'tr' | 'tt' | 'udm' | 'ug' | 'uk' | 'ur' | 'uz' | 'vi' | 'zh-hans' | 'zh-hant';
    };
    url: '/api/schema/';
};

export type ApiSchemaRetrieveResponses = {
    200: {
        [key: string]: unknown;
    };
};

export type ApiSchemaRetrieveResponse = ApiSchemaRetrieveResponses[keyof ApiSchemaRetrieveResponses];

export type DirectoutreachBetanxtVoterPolicySnapshotExportCreateData = {
    body?: never;
    path?: never;
    query: {
        /**
         * ISO 8601 datetime with timezone for which to export holdings. Must include timezone (e.g., '2024-01-15T10:30:00Z' or '2024-01-15T10:30:00+00:00'). When multiple holdings exist for the same date, selects the most recent by create_time.
         */
        valid_time: string;
    };
    url: '/directoutreach/betanxt/voter-policy-snapshot:export/';
};

export type DirectoutreachBetanxtVoterPolicySnapshotExportCreateResponses = {
    200: ExportResult;
};

export type DirectoutreachBetanxtVoterPolicySnapshotExportCreateResponse = DirectoutreachBetanxtVoterPolicySnapshotExportCreateResponses[keyof DirectoutreachBetanxtVoterPolicySnapshotExportCreateResponses];

export type DirectoutreachPolicyCreateData = {
    body: PolicyRequest;
    path?: never;
    query?: never;
    url: '/directoutreach/policy/';
};

export type DirectoutreachPolicyCreateResponses = {
    201: Policy;
};

export type DirectoutreachPolicyCreateResponse = DirectoutreachPolicyCreateResponses[keyof DirectoutreachPolicyCreateResponses];

export type DirectoutreachPolicyAvailableListData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/directoutreach/policy/available/';
};

export type DirectoutreachPolicyAvailableListResponses = {
    200: Array<PolicyResponse>;
};

export type DirectoutreachPolicyAvailableListResponse = DirectoutreachPolicyAvailableListResponses[keyof DirectoutreachPolicyAvailableListResponses];

export type DirectoutreachPolicyAvailableRetrieveData = {
    body?: never;
    path: {
        /**
         * Unique identifier for the policy
         */
        id: string;
    };
    query?: never;
    url: '/directoutreach/policy/available/{id}/';
};

export type DirectoutreachPolicyAvailableRetrieveResponses = {
    200: PolicyResponse;
};

export type DirectoutreachPolicyAvailableRetrieveResponse = DirectoutreachPolicyAvailableRetrieveResponses[keyof DirectoutreachPolicyAvailableRetrieveResponses];

export type DirectoutreachVotersRetrieveData = {
    body?: never;
    path: {
        /**
         * Unique identifier for a voter
         */
        id: string;
    };
    query?: never;
    url: '/directoutreach/voters/{id}/';
};

export type DirectoutreachVotersRetrieveErrors = {
    /**
     * No response body
     */
    404: unknown;
};

export type DirectoutreachVotersRetrieveResponses = {
    200: VoterDetailsResponse;
};

export type DirectoutreachVotersRetrieveResponse = DirectoutreachVotersRetrieveResponses[keyof DirectoutreachVotersRetrieveResponses];

export type DirectoutreachVotersHoldingsRetrieveData = {
    body?: never;
    path: {
        /**
         * Unique identifier for a voter
         */
        id: string;
    };
    query?: never;
    url: '/directoutreach/voters/{id}/holdings/';
};

export type DirectoutreachVotersHoldingsRetrieveErrors = {
    /**
     * No response body
     */
    404: unknown;
};

export type DirectoutreachVotersHoldingsRetrieveResponses = {
    200: HoldingResponse;
};

export type DirectoutreachVotersHoldingsRetrieveResponse = DirectoutreachVotersHoldingsRetrieveResponses[keyof DirectoutreachVotersHoldingsRetrieveResponses];

export type DirectoutreachVotersSelectedPolicyRetrieveData = {
    body?: never;
    path: {
        /**
         * Unique identifier for a voter
         */
        id: string;
    };
    query?: never;
    url: '/directoutreach/voters/{id}/selected-policy/';
};

export type DirectoutreachVotersSelectedPolicyRetrieveErrors = {
    /**
     * No response body
     */
    404: unknown;
};

export type DirectoutreachVotersSelectedPolicyRetrieveResponses = {
    200: SelectedPolicyResponse;
};

export type DirectoutreachVotersSelectedPolicyRetrieveResponse = DirectoutreachVotersSelectedPolicyRetrieveResponses[keyof DirectoutreachVotersSelectedPolicyRetrieveResponses];

export type DirectoutreachVotersSelectedPolicyCreateData = {
    body: SelectedPolicyRequestRequest;
    path: {
        /**
         * Unique identifier for a voter
         */
        id: string;
    };
    query?: never;
    url: '/directoutreach/voters/{id}/selected-policy/';
};

export type DirectoutreachVotersSelectedPolicyCreateErrors = {
    /**
     * No response body
     */
    400: unknown;
    /**
     * No response body
     */
    404: unknown;
};

export type DirectoutreachVotersSelectedPolicyCreateResponses = {
    200: SelectedPolicyResponse;
    201: SelectedPolicyResponse;
};

export type DirectoutreachVotersSelectedPolicyCreateResponse = DirectoutreachVotersSelectedPolicyCreateResponses[keyof DirectoutreachVotersSelectedPolicyCreateResponses];

export type DirectoutreachVotersCreateCreateData = {
    body: VoterWithHoldingsInputRequest;
    path?: never;
    query?: never;
    url: '/directoutreach/voters:create/';
};

export type DirectoutreachVotersCreateCreateResponses = {
    200: VoterWithHoldingsResponse;
    201: VoterWithHoldingsResponse;
};

export type DirectoutreachVotersCreateCreateResponse = DirectoutreachVotersCreateCreateResponses[keyof DirectoutreachVotersCreateCreateResponses];

export type InstitutionalMeetingsearchListData = {
    body?: never;
    path?: never;
    query: {
        /**
         * The pagination cursor value.
         */
        cursor?: string;
        /**
         * Search for organizations by display title (case insensitive)
         */
        organization_title_search?: string;
        /**
         * Number of results to return per page.
         */
        page_size?: number;
        /**
         * Scheme name in format 'habitats/{habitat_id}/schemes/{scheme_id}'
         */
        scheme_name: string;
        /**
         * Sort order by voting deadline (action_deadline). 'asc' shows meetings with soonest deadlines first. Default is 'asc'.
         */
        sort_order?: 'asc' | 'desc';
        /**
         * Filter meetings by status.
         * Note:
         * 'OPEN' status filters for Meetings that have an 'Action Deadline' of 00:01:00 today.
         * 'CLOSED' status filters for Meetings that have an 'Action Deadline'
         * before 'now' and a 'Meeting Date' after 'now'.
         * 'ALL' status filters for Meetings that have a 'Meeting Date' after 'now'.
         */
        status?: 'all' | 'closed' | 'open';
        /**
         * Comma-separated list of theme codes to filter by
         */
        theme_codes?: string;
    };
    url: '/institutional/meetingsearch';
};

export type InstitutionalMeetingsearchListResponses = {
    200: PaginatedMeetingList;
};

export type InstitutionalMeetingsearchListResponse = InstitutionalMeetingsearchListResponses[keyof InstitutionalMeetingsearchListResponses];

export type ProxyvotingInvestorCompositionsDifferenceListData = {
    body?: never;
    path?: never;
    query: {
        /**
         * The pagination cursor value.
         */
        cursor?: string;
        /**
         * latest composition date time
         */
        end_time: string;
        /**
         * Investors to fetch compositions for
         */
        investor_names: Array<string>;
        /**
         * original composition date time
         */
        start_time: string;
    };
    url: '/proxyvoting/investor/compositions/difference';
};

export type ProxyvotingInvestorCompositionsDifferenceListResponses = {
    200: PaginatedCompositionDifferenceResponseList;
};

export type ProxyvotingInvestorCompositionsDifferenceListResponse = ProxyvotingInvestorCompositionsDifferenceListResponses[keyof ProxyvotingInvestorCompositionsDifferenceListResponses];

export type ProxyvotingInvestorCompositionsRenderCreateData = {
    body: CompositionDifferenceResponseRequest;
    path?: never;
    query?: never;
    url: '/proxyvoting/investor/compositions/render';
};

export type ProxyvotingInvestorCompositionsRenderCreateResponses = {
    200: CompositionDifferenceResponse;
};

export type ProxyvotingInvestorCompositionsRenderCreateResponse = ProxyvotingInvestorCompositionsRenderCreateResponses[keyof ProxyvotingInvestorCompositionsRenderCreateResponses];

export type ProxyvotingPiballotsFilterListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filter ballots by workflow checker email - exact string match.
         */
        checker?: string;
        /**
         * The pagination cursor value.
         */
        cursor?: string;
        /**
         * Custodian account identifier - exact string match.
         */
        custodian_account_number?: string;
        /**
         * External ballot identifier - exact string match.
         */
        external_id?: string;
        /**
         * Filter ballots by fund manager title - case-insensitive substring match.
         */
        fund_manager_title?: string;
        /**
         * Filter ballots by fund title - case-insensitive substring match.
         */
        fund_title?: string;
        /**
         * Filter ballots by whether they have a workflow checker email assigned. True for ballots with checker, False for ballots without checker, omit for all.
         */
        has_checker?: boolean;
        /**
         * Filter ballots by whether they have a workflow submitter email assigned. True for ballots with submitter, False for ballots without submitter, omit for all.
         */
        has_submitter?: boolean;
        /**
         * Filter ballots by scope. True for in-scope (any policy support field is true), False for out-of-scope (all policy support fields are false), omit for all.
         */
        in_scope?: boolean;
        /**
         * ISIN (International Securities Identification Number) - exact match filter. Must be 12-character alphanumeric code.
         */
        isin?: string;
        /**
         * Filter ballots by whether they are linked to a general meeting. True for linked, False for unlinked, omit for all.
         */
        linked?: boolean;
        /**
         * Which field to use when ordering the results.
         */
        ordering?: string;
        /**
         * Filter ballots by organization title - case-insensitive substring match.
         */
        organization_title?: string;
        /**
         * Number of results to return per page.
         */
        page_size?: number;
        /**
         * Filter ballots by whether the 'proxy_intermediary_id' and 'proxy_intermediary_meeting_id' match the provided meeting name.
         * Expected format:'proxyIntermediaries/{proxy_intermediary_id}/meetings/{proxy_intermediary_meeting_id}'
         */
        pi_meeting_name?: string;
        /**
         * Filter ballots by proxy intermediary title - exact string match.
         */
        proxy_intermediary?: string;
        /**
         * General search across organization title, ballot external ID, and ISIN - case-insensitive substring match.
         */
        search?: string;
        /**
         * Filter ballots by whether split vote checking is required. True for required, False for not required, omit for all.
         */
        split_vote_check_required?: boolean;
        /**
         * Filter ballots by workflow status. Multiple values can be selected to match any of the specified statuses.
         *
         * * `invalid` - Invalid
         * * `not_submitted_in_testing` - Not Submitted in Testing
         * * `not_submitted_unvoted` - Not Submitted Unvoted
         * * `not_submitted_no_entitlements` - Not Submitted No Entitlements
         * * `not_submitted_undefined` - Not Submitted Undefined
         * * `needs_attention` - Needs Attention
         * * `ineligible` - Ineligible
         * * `cancelled` - Cancelled
         * * `meeting_needs_refining` - Meeting Needs Refining
         * * `meeting_voting_enabled` - Meeting Voting Enabled
         * * `entitled_to_vote` - Entitled to Vote
         * * `ready_for_split_vote` - Ready for Split Vote
         * * `late_vote_process` - Late Vote Process
         * * `split_vote_awaiting_check` - Split Vote Awaiting Check
         * * `split_vote_submitted` - Split Vote Submitted
         * * `successful` - Successful
         * * `rejected` - Rejected
         * * `overridden` - Overridden
         * * `in_testing` - In Testing
         */
        status?: Array<'cancelled' | 'entitled_to_vote' | 'in_testing' | 'ineligible' | 'invalid' | 'late_vote_process' | 'meeting_needs_refining' | 'meeting_voting_enabled' | 'needs_attention' | 'not_submitted_in_testing' | 'not_submitted_no_entitlements' | 'not_submitted_undefined' | 'not_submitted_unvoted' | 'overridden' | 'ready_for_split_vote' | 'rejected' | 'split_vote_awaiting_check' | 'split_vote_submitted' | 'successful'>;
        /**
         * Filter ballots by workflow submitter email - exact string match.
         */
        submitter?: string;
        /**
         * Filter ballots with vote deadline after or equal to this UTC datetime.
         */
        vote_deadline_after?: Date;
        /**
         * Filter ballots with vote deadline before or equal to this UTC datetime.
         */
        vote_deadline_before?: Date;
    };
    url: '/proxyvoting/piballots/filter';
};

export type ProxyvotingPiballotsFilterListResponses = {
    200: PaginatedPiBallotSearchList;
};

export type ProxyvotingPiballotsFilterListResponse = ProxyvotingPiballotsFilterListResponses[keyof ProxyvotingPiballotsFilterListResponses];

export type ProxyvotingPimeetingsFilterListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filter meetings by assigned checker user email address - exact match.
         */
        assigned_checker_user_id?: string;
        /**
         * Filter meetings by assigned refiner user email address - exact match.
         */
        assigned_refiner_user_id?: string;
        /**
         * The pagination cursor value.
         */
        cursor?: string;
        /**
         * Filter meetings by ballot presence. True for meetings with ballots, False for meetings without ballots, omit for all.
         */
        has_ballots?: boolean;
        /**
         * Filter meetings by checker presence. True for meetings with checker, False for meetings without checker, omit for all.
         */
        has_checker?: boolean;
        /**
         * Filter meetings by refiner presence. True for meetings with refiner, False for meetings without refiner, omit for all.
         */
        has_refiner?: boolean;
        /**
         * Filter meetings by scope. True for in-scope (any policy support field is true), False for out-of-scope (all policy support fields are false), omit for all.
         */
        in_scope?: boolean;
        /**
         * Filter meetings by whether they are linked to Tumelo meetings. True for linked, False for unlinked, omit for all.
         */
        linked_to_tumelo_meeting?: boolean;
        /**
         * Filter meetings created after or equal to this UTC datetime.
         */
        meeting_create_time_after?: Date;
        /**
         * Filter meetings created before or equal to this UTC datetime.
         */
        meeting_create_time_before?: Date;
        /**
         * Which field to use when ordering the results.
         */
        ordering?: string;
        /**
         * Number of results to return per page.
         */
        page_size?: number;
        /**
         * Proxy intermediary managing the meetings. Options: Proxymity, ProxyEdge, ProxyExchange, or omit for all.
         *
         * * `Proxymity` - Proxymity
         * * `ProxyEdge` - ProxyEdge
         * * `ProxyExchange` - ProxyExchange
         */
        proxy_intermediary?: 'ProxyEdge' | 'ProxyExchange' | 'Proxymity';
        /**
         * General search across meeting external ID, organization title, and security IDs - case-insensitive substring match. Maximum 255 characters.
         */
        search?: string;
        /**
         * Filter meetings by workflow status. Multiple values can be selected to match any of the specified statuses.
         *
         * * `invalid` - Invalid
         * * `new` - New
         * * `unlinked` - Unlinked
         * * `unsupported` - Unsupported
         * * `needs_attention` - Needs Attention
         * * `cancelled` - Cancelled
         * * `cancellation_incomplete` - Cancellation Incomplete
         * * `ready_for_refinement` - Ready for Refinement
         * * `refinement_complete` - Refinement Complete
         * * `manual_voting_enabled` - Manual Voting Enabled
         * * `entitled_to_vote` - Entitled to Vote
         * * `ready_for_split_vote` - Ready for Split Vote
         * * `split_vote_submitted` - Split Vote Submitted
         * * `successful` - Successful
         * * `rejected` - Rejected
         * * `overridden` - Overridden
         * * `partial_success` - Partial Success
         */
        status?: Array<'cancellation_incomplete' | 'cancelled' | 'entitled_to_vote' | 'invalid' | 'manual_voting_enabled' | 'needs_attention' | 'new' | 'overridden' | 'partial_success' | 'ready_for_refinement' | 'ready_for_split_vote' | 'refinement_complete' | 'rejected' | 'split_vote_submitted' | 'successful' | 'unlinked' | 'unsupported'>;
        /**
         * Filter meetings with submission deadline after or equal to this UTC datetime, where the latest valid_to SII date is before this datetime, with an 8 month buffer.
         */
        submission_deadline_after?: Date;
        /**
         * Filter meetings with submission deadline before or equal to this UTC datetime.
         */
        submission_deadline_before?: Date;
    };
    url: '/proxyvoting/pimeetings/filter';
};

export type ProxyvotingPimeetingsFilterListResponses = {
    200: PaginatedPiMeetingSearchList;
};

export type ProxyvotingPimeetingsFilterListResponse = ProxyvotingPimeetingsFilterListResponses[keyof ProxyvotingPimeetingsFilterListResponses];

export type ProxyvotingSviProposalOverwriteCreateData = {
    body: SplitVoteInstructionProposalOverwriteRequest;
    path?: never;
    query?: never;
    url: '/proxyvoting/svi/proposal/overwrite';
};

export type ProxyvotingSviProposalOverwriteCreateResponses = {
    201: SplitVoteInstructionProposalOverwrite;
};

export type ProxyvotingSviProposalOverwriteCreateResponse = ProxyvotingSviProposalOverwriteCreateResponses[keyof ProxyvotingSviProposalOverwriteCreateResponses];

export type ProxyvotingSviProposalOverwriteReasonsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * The pagination cursor value.
         */
        cursor?: string;
        /**
         * Which field to use when ordering the results.
         */
        ordering?: string;
    };
    url: '/proxyvoting/svi/proposal/overwrite/reasons';
};

export type ProxyvotingSviProposalOverwriteReasonsListResponses = {
    200: PaginatedSplitVoteInstructionOverwriteReasonList;
};

export type ProxyvotingSviProposalOverwriteReasonsListResponse = ProxyvotingSviProposalOverwriteReasonsListResponses[keyof ProxyvotingSviProposalOverwriteReasonsListResponses];
